import { createModel } from '@rematch/core';
import { generatePath } from 'react-router-dom';
import { toast } from 'react-toastify';

// Constants
import URL from '../../constants/api-constants';

// Types
import type { RootModel } from './index';

// Http
import http from '../http/http-common';
import { Organization } from './organizations';
import { IUser } from './user';
import axios, { AxiosError, AxiosRequestConfig, GenericAbortSignal } from 'axios';
import { Location } from './locations';
import { ChartPreference } from './chart-preference';

export type Member = {
  id: string;
  userId: string;
  organizationId: string;
  role: string;
  email: string;
  first_name: string;
  last_name: string;
  full_name?: string;
  active: boolean;
  organization?: Organization | null;
  locations?: Location[];
  user?: IUser | null;
  is_show_notes?: boolean;
  chart_preferences?: ChartPreference[];
};

const DEFAULT_STATE: {
  members: Member[];
  member: Member | null;
  currentUserMember: Member | null;
} = {
  members: [],
  member: null,
  currentUserMember: null,
};

export const members = createModel<RootModel>()({
  state: DEFAULT_STATE,
  reducers: {
    set(state, payload) {
      return {
        ...state,
        ...payload,
      };
    },
    changeViewNotes(state, payload) {
      return {
        members: [...state.members],
        member: { ...state.member },
        currentUserMember: {
          ...state.currentUserMember,
          is_show_notes: payload,
        },
      };
    },
    resetMembers(state) {
      return {
        ...state,
        members: DEFAULT_STATE.members,
      };
    },
    resetMember(state) {
      return {
        ...state,
        member: DEFAULT_STATE.member,
      };
    },
  },
  effects: (dispatch) => {
    return {
      async getMembers() {
        try {
          const response = await http(URL.MEMBERS);

          if (response.status === 200) {
            dispatch.members.set({
              members: response.data,
            });
          }
        } catch (error: unknown) {
          if (error instanceof AxiosError) {
            toast.error(error.response?.data?.message || 'An unexpected error occurred');
          } else if (error instanceof Error) {
            toast.error(error.message);
          } else {
            toast.error('An unexpected error occurred');
          }
          throw error;
        }
      },

      async createMember(payload) {
        try {
          await http.post(URL.INVITE_MEMBER, payload);
          toast.success('Member created successfully');
        } catch (error: unknown) {
          if (error instanceof AxiosError) {
            toast.error(error.response?.data?.message || 'An unexpected error occurred');
          } else if (error instanceof Error) {
            toast.error(error.message);
          } else {
            toast.error('An unexpected error occurred');
          }
          throw error;
        }
      },

      async updateMember({ id, formData }, rootState) {
        try {
          const response = await http.put(generatePath(URL.MEMBER, { id }), formData);
          toast.success('Member updated successfully');
          // The update endpoint doesn't return the `user` relation, so it has
          // no email/name fields - merge onto the existing member instead of
          // replacing it outright, or those fields (used by the breadcrumb)
          // go blank right after a successful save.
          dispatch.members.set({
            member: {
              ...rootState.members.member,
              ...response.data,
            },
          });
        } catch (error: unknown) {
          if (error instanceof AxiosError) {
            toast.error(error.response?.data?.message || 'An unexpected error occurred');
          } else if (error instanceof Error) {
            toast.error(error.message);
          } else {
            toast.error('An unexpected error occurred');
          }
          throw error;
        }
      },

      async resetMemberPassword({ id, password }) {
        try {
          await http.put(generatePath(URL.RESET_MEMBER_PASSWORD, { id }), { password });
          toast.success('Password reset successfully');
        } catch (error: unknown) {
          if (error instanceof AxiosError) {
            toast.error(error.response?.data?.message || 'An unexpected error occurred');
          } else if (error instanceof Error) {
            toast.error(error.message);
          } else {
            toast.error('An unexpected error occurred');
          }
          throw error;
        }
      },

      async reInviteMember({ id }) {
        try {
          await http.get(generatePath(URL.RE_INVITE_MEMBER, { id }));
          toast.success('Member reinvited successfully');
        } catch (error: unknown) {
          if (error instanceof AxiosError) {
            toast.error(error.response?.data?.message || 'An unexpected error occurred');
          } else if (error instanceof Error) {
            toast.error(error.message);
          } else {
            toast.error('An unexpected error occurred');
          }
          throw error;
        }
      },
      async getMember(params: { memberId: string; signal?: GenericAbortSignal }) {
        try {
          const path = generatePath(URL.MEMBER, { id: params.memberId });
          const config: AxiosRequestConfig<{ signal?: GenericAbortSignal }> = {};
          if (params?.signal) {
            config.signal = params.signal;
          }
          const response = await http.get(path, config);
          const member = {
            ...response.data,
            email: response.data.user.email,
            first_name: response.data.user.first_name,
            last_name: response.data.user.last_name,
            full_name: `${response.data.user.first_name} ${response.data.user.last_name}`.trim(),
          };
          dispatch.members.set({ member });

          return { member };
        } catch (error) {
          if (axios.isCancel(error)) {
            return;
          }

          if (error instanceof AxiosError) {
            toast.error(error.response?.data?.message || 'An unexpected error occurred');
          } else if (error instanceof Error) {
            toast.error(error.message);
          } else {
            toast.error('An unexpected error occurred');
          }
          throw error;
        }
      },

      async deleteMember(id: string, state) {
        try {
          const memberResponse = await http.get(generatePath(URL.MEMBER, { id }));
          const userId = memberResponse?.data?.userId || memberResponse?.data?.user?.id;

          if (!userId) {
            throw new Error('Unable to resolve user id for member deletion');
          }

          await http.delete(generatePath(URL.USER_PROFILE, { id: userId }));
          const currentMembers = state.members.members.filter((member) => member.id !== id);
          dispatch.members.set({ members: currentMembers });
          toast.success('Member deleted successfully');
        } catch (error: unknown) {
          if (error instanceof AxiosError) {
            toast.error(error.response?.data?.message || 'An unexpected error occurred');
          } else if (error instanceof Error) {
            toast.error(error.message);
          } else {
            toast.error('An unexpected error occurred');
          }
          throw error;
        }
      },

      async updateViewNotes({ id, is_show_notes }) {
        try {
          dispatch.members.changeViewNotes(is_show_notes);
          toast.success('The notes view has been updated successfully');

          await http.put(generatePath(URL.MEMBER, { id }), { is_show_notes });
        } catch (error: unknown) {
          if (error instanceof AxiosError) {
            toast.error(error.response?.data?.message || 'An unexpected error occurred');
          } else if (error instanceof Error) {
            toast.error(error.message);
          } else {
            toast.error('An unexpected error occurred');
          }
          throw error;
        }
      },
    };
  },
});
